// Manifesto page — philosophical centrepiece of the Paisums Technology site.
import { ArrowRight } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import InnerHero from "../components/InnerHero";
import Reveal from "../components/Reveal";
import { VIDEOS } from "../lib/media";

export default function Manifesto() {
  return (
    <div className="bg-black">
      <Nav route="inner" />
      <InnerHero
        video={VIDEOS.featured}
        eyebrow="Our Manifesto"
        headline="AI that earns its"
        italicWord="place."
        lede="We build AI for the people who run the hard industries. Not for the demo. Not for the pitch deck. For the floor, the field and the fleet."
      />

      <div className="bg-black pt-24 pb-4">
        <div className="max-w-3xl mx-auto px-6">

          {/* Section 1 */}
          <Reveal>
            <div className="text-white/40 text-xs tracking-widest uppercase mb-3">Why we exist</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-instrument text-3xl md:text-4xl text-white tracking-tight">
              Industry deserves better than bolt-on AI.
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-white/70 text-base leading-relaxed mt-5">
              AI adoption in mining, defence and construction has stalled because most vendors sell tools
              that fit demos but break in the field. We started Paisums because we saw senior engineers
              waste years on AI projects that never shipped.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <p className="text-white/70 text-base leading-relaxed mt-5">
              We do it differently. We embed with your team, learn the actual operating constraints, and
              build systems that go live, stay live and create measurable outcomes from day one.
            </p>
          </Reveal>

          <div className="hairline my-16" />

          {/* Section 2 */}
          <Reveal>
            <div className="text-white/40 text-xs tracking-widest uppercase mb-3">What we believe</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-instrument text-3xl md:text-4xl text-white tracking-tight">
              Production over promises.
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-white/70 text-base leading-relaxed mt-5">
              A model that works in a notebook is not a product. A dashboard that impresses the board is
              not a system. We believe AI earns its place by doing real work, reliably, under the
              conditions your business actually operates in.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <p className="text-white/70 text-base leading-relaxed mt-5">
              That means senior engineers on every engagement. It means your data stays sovereign. It
              means we do not hand you a report and leave.
            </p>
          </Reveal>

          <div className="hairline my-16" />

          {/* Section 3 */}
          <Reveal>
            <div className="text-white/40 text-xs tracking-widest uppercase mb-3">Who we build for</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-instrument text-3xl md:text-4xl text-white tracking-tight">
              The industries that keep the country running.
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-white/70 text-base leading-relaxed mt-5">
              We work with mining operators, defence subcontractors, heavy machinery businesses, GIS teams
              and construction firms. These sectors move slowly for good reasons. Safety matters.
              Compliance matters. Uptime matters.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <p className="text-white/70 text-base leading-relaxed mt-5">
              We respect that. Our AI systems are built to pass procurement, satisfy security review and
              hold up under operational pressure.
            </p>
          </Reveal>

          <div className="hairline my-16" />

          {/* Section 4 */}
          <Reveal>
            <div className="text-white/40 text-xs tracking-widest uppercase mb-3">Our commitment</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-instrument text-3xl md:text-4xl text-white tracking-tight">
              We ship. You own it.
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-white/70 text-base leading-relaxed mt-5">
              Every engagement ends with working software, full documentation, and a team that knows how
              to operate it. No lock-in. No dependency on our continued involvement unless you want it.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <p className="text-white/70 text-base leading-relaxed mt-5">
              If we are not the right fit for your problem, we will tell you that upfront and point you
              to someone who is.
            </p>
          </Reveal>

        </div>
      </div>

      <section className="bg-black px-6 pt-16 pb-24">
        <div className="max-w-2xl mx-auto liquid-glass rounded-3xl p-10 text-center">
          <h3 className="font-instrument text-3xl text-white">
            Ready to build something that lasts?
          </h3>
          <p className="text-white/60 text-base mt-4">
            Start with a 30-minute discovery call. No slide deck. Just an engineer.
          </p>
          <a
            href="/#contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3
                       bg-white text-black text-sm font-medium hover:bg-white/90
                       transition-colors"
          >
            Book a discovery <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
